import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { FaPlus } from "react-icons/fa";
import toast from "react-hot-toast";
import { Shimmer } from "react-shimmer";
import { useNavigate } from "react-router-dom";
import { taskHeaders } from "../../constants/CONSTANT";
import JajotApi from "../../api/JajotApi";
import RecordDataTable from "../RecordDataTable";
import { Col, Container, Row, Button } from "react-bootstrap";
import RecordModal from '../common/RecordModal';
import TaskWizard from './TaskWizard';

const TaskList = () => {
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedTask, setSelectedTask] = useState(null);
    const [isOpenRecordModalOpen, setIsRecordModalOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        fetchTasks();
    }, []);

    const fetchTasks = async () => {
        try {
            setIsLoading(true);
            const result = await JajotApi.getTasksRecords(null, 'Task');
            if (result.success) {
                setTasks(result.records || []);
            } else {
                setTasks([]);
                toast.error("No task found");
            }
        } catch (error) {
            console.log("Error fetching tasks:", error);
            toast.error("Failed to fetch tasks");
            setTasks([]);
        } finally {
            setIsLoading(false);
        }
    };

    const normalizedTasks = useMemo(() => {
        return tasks.map(task => ({
            ...task,
            Status: task.Status__c || '',
            Priority: task.Priority__c || '',
            StartDate: task.Start_Date__c ? new Date(task.Start_Date__c).toISOString() : null,
            EndDate: task.End_Date__c ? new Date(task.End_Date__c).toISOString() : null,
        }));
    }, [tasks]);

    const navigateToEdit = (task = {}) => {
        const mappedTask = {
            Id: task?.Id || null,
            Name: task?.Name,
            Title: task?.Title__c || null,
            Status: task?.Status__c || "New",
            OwnerId: task?.OwnerId__c || null,
            StartDate: task?.Start_Date__c,
            EndDate: task?.End_Date__c,
            Priority: task?.Priority__c || "",
            Description: task?.Description__c || null,
            Assignees: task?.Task_Assignees__r?.records?.map(element => ({
                recordId: element?.Id,
                assigneeId: element?.ContactId__c,
            })) || [],
        };

        navigate(`/task/edit`, { state: { existingTask: mappedTask } });
    };

    const handleDelete = async (id) => {
        try {
            const result = await JajotApi.deleteTaskBy(id);
            if (!result.success) {
                toast.error("Failed to delete task");
                return;
            }
            setTasks(prev => prev.filter(o => o.Id !== id));
            toast.success("Record Deleted Successfully!");
        } catch (err) {
            console.error("Delete task error:", err);
            toast.error("Error deleting task");
        }
    };

    const handleStatusChange = (id, newStatus) => {
        const updatedTasks = tasks.map(task =>
            task.Id === id ? { ...task, Status__c: newStatus } : task
        );
        setTasks(updatedTasks);
        toast.success("Task status changed");
    };

    /* ---------------- FILTER CONFIG (MATCH NORMALIZED KEYS) ---------------- */

    const statusOptions = [
        { value: "New", label: "New" },
        { value: "In Progress", label: "In Progress" },
        { value: "Completed", label: "Completed" },
        { value: "Cancelled", label: "Cancelled" },
    ];

    const priorityOptions = [
        { value: "Low", label: "Low" },
        { value: "Medium", label: "Medium" },
        { value: "High", label: "High" },
    ];

    const filtersConfig = [
        { key: "StartDate", label: "Start Date", type: "date" },
        { key: "EndDate", label: "End Date", type: "date" },
        {
            key: "Status",
            label: "Status",
            type: "select",
            options: statusOptions,
        },
        {
            key: "Priority",
            label: "Priority",
            type: "select",
            options: priorityOptions,
        },

    ];

    return (
        <Fragment>
            <Container fluid className="py-4">
                <div className="bg-white shadow-sm rounded p-3">
                    <Row className="mb-4 align-items-center g-2">
                        <Col xs={12} md>
                            <h4 className="fw-bold text-primary mb-0">Tasks Records</h4>
                            <small className="text-muted">Manage and view all tasks</small>
                        </Col>
                        <Col xs={12} md="auto">
                            <Button
                                size="sm"
                                variant="success"
                                disabled={isLoading}
                                onClick={() => navigateToEdit()}
                            >
                                <FaPlus className="me-1" /> Add New Task
                            </Button>
                        </Col>
                    </Row>

                    {isLoading ? (
                        <div className="p-3">
                            <Shimmer width={600} height={30} className="mb-3" />
                            <Shimmer width={-1} height={20} className="mb-2" />
                            <Shimmer width={-1} height={20} className="mb-2" />
                            <Shimmer width={-1} height={20} />
                        </div>
                    ) : normalizedTasks.length > 0 ? (
                        <div className="table-responsive">
                            <RecordDataTable
                                tableHeaders={taskHeaders}
                                tableData={normalizedTasks}
                                handleRowClick={(row) => {
                                    setSelectedTask(row);
                                    setIsRecordModalOpen(true);
                                }}
                                permission={{ update: true, delete: true }}
                                filtersConfig={filtersConfig}
                                onStatusChange={handleStatusChange}
                                onDelete={handleDelete}
                                handleEdit={navigateToEdit}
                            />
                        </div>
                    ) : (
                        <div className="text-center text-muted py-5">
                            <h6>No tasks records found</h6>
                        </div>
                    )}
                </div>
            </Container>

            {isOpenRecordModalOpen && (
                <RecordModal
                    show
                    onHide={() => {
                        setSelectedTask(null);
                        setIsRecordModalOpen(false);
                    }}
                    record={selectedTask}
                    header={taskHeaders}
                    defaultType="tasks"
                />
            )}
        </Fragment>
    );
};

export default TaskList;
